import React, { useState } from 'react'
import { Box, Typography, Chip, Paper, CircularProgress } from '@mui/material'
import {
  Action,
  ActionStatus,
} from '@/features/patient-actions/types/patientActions'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import {
  ACTION_STATUS_LABELS,
  ACTION_GROUP_LABELS,
} from '../../../constants/patientConstants'

interface ActionCardProps {
  action: Action
  onStatusUpdate: (newStatus: ActionStatus) => Promise<void>
  maxWidth: string | number
  showGroupChip: boolean
}

// デフォルト値
const DEFAULT_MAX_WIDTH = '100%'
const DEFAULT_SHOW_GROUP_CHIP = true

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  onStatusUpdate,
  maxWidth = DEFAULT_MAX_WIDTH,
  showGroupChip = DEFAULT_SHOW_GROUP_CHIP,
}) => {
  const [updating, setUpdating] = useState(false)

  const handleStatusChange = async (newStatus: ActionStatus) => {
    if (newStatus === action.status) return

    setUpdating(true)
    try {
      await onStatusUpdate(newStatus)
    } finally {
      setUpdating(false)
    }
  }

  // コンパクトレイアウト用のステータス行分割
  const firstRowStatuses: ActionStatus[] = ['pending', 'planned', 'in_progress']
  const secondRowStatuses: ActionStatus[] = ['done', 'declined']

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: colorTokens.bg.default,
        border: `1px solid ${colorTokens.border.default}`,
        borderRadius: `${RADIUS.md}px`,
        opacity:
          action.status === 'declined' || action.status === 'done' ? 0.7 : 1,
        maxWidth: maxWidth,
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color:
                action.status === 'declined' || action.status === 'done'
                  ? colorTokens.fg.muted
                  : colorTokens.fg.default,
              textDecoration:
                action.status === 'declined' || action.status === 'done'
                  ? 'line-through'
                  : 'none',
            }}
          >
            {action.label}
          </Typography>
          {showGroupChip && (
            <Chip
              label={ACTION_GROUP_LABELS[action.group]}
              size="small"
              sx={{
                height: 20,
                backgroundColor: 'rgba(55,53,47,0.04)',
                color: colorTokens.accent.primary,
                border: `1px solid ${colorTokens.accent.primary}`,
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          )}
        </Box>

        {action.description.length > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: colorTokens.fg.muted,
              mb: 0.5,
            }}
          >
            詳細: {action.description.join(' / ')}
          </Typography>
        )}

        {action.proposal_reason.length > 0 && (
          <Typography
            variant="body2"
            sx={{
              color: colorTokens.fg.muted,
              mb: 1,
            }}
          >
            理由: {action.proposal_reason.join(' / ')}
          </Typography>
        )}

        {/* ステータス選択チップ */}
        <Box sx={{ mt: 1.5, width: '100%', overflow: 'hidden' }}>
          {/* 第1行: 保留中、計画済み、実施中 */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              mb: 0.5,
              justifyContent: 'flex-start',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            {firstRowStatuses.map((status) => (
              <Chip
                key={status}
                label={ACTION_STATUS_LABELS[status]}
                size="small"
                disabled={updating}
                onClick={() => handleStatusChange(status)}
                sx={{
                  backgroundColor:
                    action.status === status
                      ? 'rgba(25, 118, 210, 0.08)'
                      : colorTokens.bg.default,
                  color:
                    action.status === status
                      ? colorTokens.accent.primary
                      : colorTokens.fg.muted,
                  border: `1px solid ${
                    action.status === status
                      ? colorTokens.accent.primary
                      : colorTokens.border.default
                  }`,
                  cursor: updating ? 'not-allowed' : 'pointer',
                  opacity: updating ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor:
                      action.status === status
                        ? 'rgba(25, 118, 210, 0.12)'
                        : 'rgba(25, 118, 210, 0.04)',
                    borderColor: colorTokens.accent.primary,
                    color: colorTokens.accent.primary,
                  },
                  height: 30,
                  minWidth: 60,
                  width: 'auto',
                  '& .MuiChip-label': {
                    px: 1.5,
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            ))}
          </Box>
          {/* 第2行: 完了、却下 */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'flex-start',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            {secondRowStatuses.map((status) => (
              <Chip
                key={status}
                label={ACTION_STATUS_LABELS[status]}
                size="small"
                disabled={updating}
                onClick={() => handleStatusChange(status)}
                sx={{
                  backgroundColor:
                    action.status === status
                      ? 'rgba(25, 118, 210, 0.08)'
                      : colorTokens.bg.default,
                  color:
                    action.status === status
                      ? colorTokens.accent.primary
                      : colorTokens.fg.muted,
                  border: `1px solid ${
                    action.status === status
                      ? colorTokens.accent.primary
                      : colorTokens.border.default
                  }`,
                  cursor: updating ? 'not-allowed' : 'pointer',
                  opacity: updating ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor:
                      action.status === status
                        ? 'rgba(25, 118, 210, 0.12)'
                        : 'rgba(25, 118, 210, 0.04)',
                    borderColor: colorTokens.accent.primary,
                    color: colorTokens.accent.primary,
                  },
                  height: 30,
                  minWidth: 60,
                  width: 'auto',
                  '& .MuiChip-label': {
                    px: 1.5,
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {updating && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <CircularProgress size={16} />
          </Box>
        )}
      </Box>
    </Paper>
  )
}

export default ActionCard
